import React, { useState } from "react";
import { Trans } from "@lingui/react/macro";
import TimeSelect from "../TimeSelect";
import MaterialenLijst, { materialenForSubmit } from "./MaterialenLijst";

const MAX_FILE_SIZE_MB = 10;

const WorkEntryEditor = ({
  copyOptions,
  selectedCopyId,
  setSelectedCopyId,
  selectedDate,
  handleDateChange,
  loading,
  handleSubmit,
  formData,
  handleInputChange,
  isAfterWorkTimeModified,
  files,
  handleFileChange,
  handleRemoveFile,
  isImage,
  isEditing,
  fileError,
  projectFinishedError,
  uploadProgress,
}) => {
  const [rejectedFiles, setRejectedFiles] = useState([]);

  const _handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);

    const validFiles = [];
    const tooBigFiles = [];

    selected.forEach((file) => {
      const isImage = file.type.startsWith("image/");

      if (file.size <= MAX_FILE_SIZE_MB * 1024 * 1024 || isImage)
        validFiles.push(file);
      else tooBigFiles.push(file);
    });

    setRejectedFiles(tooBigFiles);
    handleFileChange(validFiles);
  };

  return (
    <div>
      {copyOptions.length > 0 && (
        <div>
          <h2>
            <Trans>Copy Delivery Note from:</Trans>
          </h2>
          {copyOptions.map(([username, werkId]) => (
            <div
              key={werkId}
              className={`copy-note-item ${
                selectedCopyId === werkId ? "selected" : ""
              }`}
            >
              <input
                type="checkbox"
                id={`copy_${werkId}`}
                value={werkId}
                checked={selectedCopyId === werkId}
                onChange={() =>
                  setSelectedCopyId(selectedCopyId === werkId ? null : werkId)
                }
              />
              <label htmlFor={`copy_${werkId}`}>{username}</label>
            </div>
          ))}
        </div>
      )}

      {!selectedCopyId && (
        <div className="work-entries-section">
          {loading ? (
            <div className="upload-progress-card">
              <h3 className="upload-progress-title">
                <Trans>Uploading Files</Trans> {Math.round(uploadProgress)}%
              </h3>
              <p className="upload-progress-warning">
                <Trans>
                  Don't reload or close this tab until the upload is complete!
                </Trans>
              </p>

              <div className="progress-bar-container">
                <div className="progress-bar">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="work-header">
                <Trans>Create Delivery Note for</Trans>{" "}
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="date-selector-inline"
                />
              </h2>
              <form onSubmit={(e) => handleSubmit(e)} className="form">
                <label>
                  <Trans>Preparation time in warehouse:</Trans>
                </label>
                <TimeSelect
                  name="preparation"
                  value={formData.preparation}
                  handleInputChange={handleInputChange}
                />

                <label>
                  <Trans>Transport Time:</Trans>
                </label>
                <TimeSelect
                  name="transport"
                  value={formData.transport}
                  handleInputChange={handleInputChange}
                />

                <label>
                  <Trans>Work start time:</Trans>
                </label>
                <input
                  type="time"
                  className="input-time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                />

                <label>
                  <Trans>Total break time:</Trans>
                </label>
                <TimeSelect
                  name="break"
                  value={formData.break}
                  handleInputChange={handleInputChange}
                />

                <label>
                  <Trans>Work end time:</Trans>
                </label>
                <input
                  type="time"
                  className="input-time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                />

                <label>
                  <Trans>Time in warehouse after work:</Trans>
                </label>
                <TimeSelect
                  name="warehouse"
                  value={formData.warehouse}
                  handleInputChange={handleInputChange}
                />
                {isAfterWorkTimeModified && (
                  <p className="warning-text">
                    <Trans>
                      Be careful, only fill in for exceptional projects!
                    </Trans>
                  </p>
                )}

                <label>
                  <Trans>Used materials:</Trans>
                </label>
                {/* 27/10/2026 (Bartel): het vrije tekstveld blijft; het is niet meer verplicht zodra de
                    lijst eronder minstens één materiaal bevat. */}
                <textarea
                  className="used-materials-textarea"
                  name="materials"
                  value={formData.materials}
                  onChange={handleInputChange}
                  required={materialenForSubmit(formData.materialen).length === 0}
                ></textarea>
                <MaterialenLijst
                  materialen={formData.materialen}
                  onChange={(list) =>
                    handleInputChange({ target: { name: "materialen", value: list } })
                  }
                />

                <label>
                  <Trans>Work Description:</Trans>
                </label>
                <textarea
                  name="description_work"
                  value={formData.description_work}
                  onChange={handleInputChange}
                  required
                ></textarea>

                <label>
                  <Trans>Project Finished:</Trans>
                </label>
                <div className="select-container">
                  <select
                    name="finished"
                    className="time-select"
                    value={
                      formData.finished === undefined
                        ? "undefined"
                        : formData.finished.toString()
                    }
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: "finished",
                          value:
                            e.target.value === "true"
                              ? true
                              : e.target.value === "false"
                              ? false
                              : undefined,
                        },
                      })
                    }
                  >
                    <option value="undefined">
                      <Trans>Not Selected</Trans>
                    </option>
                    <option value="true">
                      <Trans>Yes</Trans>
                    </option>
                    <option value="false">
                      <Trans>No</Trans>
                    </option>
                  </select>
                </div>

                {projectFinishedError && (
                  <h2 className="warning-text">
                    <Trans>Please select if the project is finished</Trans>
                  </h2>
                )}

                <div className="upload-container">
                  <h3>
                    <Trans>Upload Files:</Trans>
                  </h3>
                  <label htmlFor="file-upload" className="upload-button">
                    <span className="plus-icon">+</span>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    name="files"
                    onChange={_handleFileChange}
                    multiple
                    style={{ display: "none" }}
                  />
                </div>
                {rejectedFiles.length > 0 && (
                  <div className="warning-bubble">
                    <h2 className="warning-text">
                      <Trans>
                        The following files were too large and not added (max{" "}
                        {MAX_FILE_SIZE_MB} MB).
                      </Trans>
                    </h2>
                    <h2 className="warning-text">
                      <Trans>
                        Please send them manually to Bartel via Whatsapp!:
                      </Trans>
                    </h2>
                    <ul style={{ paddingLeft: "1.2rem" }}>
                      {rejectedFiles.map((file, idx) => (
                        <li key={idx}>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)}{" "}
                          MB)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {files.length > 0 && (
                  <div className="file-preview">
                    {/* Files */}
                    <h4>
                      <Trans>Files:</Trans>
                    </h4>
                    <ul className="non-image-files">
                      {files
                        .filter((file) => !isImage(file)) // Filter non-image files
                        .map((file, index) => (
                          <li key={index} className="file-item">
                            {file.name || `File ${index + 1}`}
                            {!isEditing && (
                              <button
                                className="remove-file-button"
                                onClick={() => handleRemoveFile(index)}
                              >
                                ✖
                              </button>
                            )}
                          </li>
                        ))}
                    </ul>

                    {/* Images */}
                    <h4>
                      <Trans>Images:</Trans>
                    </h4>
                    <div className="image-previews">
                      {files
                        .filter((file) => isImage(file)) // Filter image files
                        .map((photo, index) => {
                          const isBase64 =
                            typeof photo === "object" && photo.content;
                          const src = isBase64
                            ? `data:image/jpeg;base64,${photo.content}`
                            : URL.createObjectURL(photo);
                          return (
                            <div key={index} className="image-container">
                              {!isEditing && (
                                <button
                                  className="remove-image-button"
                                  onClick={() => handleRemoveFile(index)}
                                >
                                  ✖
                                </button>
                              )}
                              <img
                                src={src}
                                alt={`preview-${index}`}
                                className="photo"
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {fileError && (
                  <h2 className="warning-text">
                    <Trans>Upload at least one file</Trans>
                  </h2>
                )}
              </form>
              <button
                onClick={(e) => handleSubmit(e)}
                className="submit-button"
              >
                <Trans>Submit</Trans>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkEntryEditor;
